import React, { useEffect, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import classNames from 'classnames'
import * as R from 'ramda'
import { type Nil, NumberUtils } from '@pbt/pbt-ui-components'

import {
  type GroupedLineItemArrangementInput,
  LineItemType,
  type RetailOrderLineItem,
} from '~/api/graphql/generated/types'
import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import {
  getInvoiceGroupSubTotal,
  getIsRetailOrderLineItem,
  isGroupedInvoiceItem,
} from '~/components/dashboard/invoices/invoiceUtils'
import { ConfirmAlertType } from '~/constants/DialogNames'
import {
  editRetailOrderLineItem,
  rearrangeGroupedLineItems,
  toggleInvoiceGroupExpanded,
} from '~/store/actions/finance'
import {
  deleteChargeSheetItems,
  editChargeSheetItem,
  getClientFinanceLoading,
} from '~/store/duck/clientFinanceData'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { getCurrentUserId } from '~/store/reducers/auth'
import {
  getFinanceIsFetching,
  getFinanceIsSaving,
} from '~/store/reducers/finance'
import { getOrdersIsSending } from '~/store/reducers/orders'
import { getPatientName } from '~/store/reducers/patients'
import { getClientId, getPatientId, getSoapId } from '~/store/reducers/soap'
import type { InvoiceLineItem } from '~/types'

import ChirpSoapChargesTableRow from './ChirpSoapChargesTableRow'

import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import styles from './ChirpSoapChargesTable.module.scss'

export interface ChirpSoapChargesTableProps {
  chargesId: string | Nil
  editDisabled?: boolean
  isExpanded?: boolean
  items: (InvoiceLineItem | RetailOrderLineItem)[]
}

const ChirpSoapChargesTable = ({
  chargesId,
  editDisabled = false,
  isExpanded = false,
  items,
}: ChirpSoapChargesTableProps) => {
  const { t } = useTranslation(['Common', 'Payments'])
  const dispatch = useDispatch()
  const soapId = useSelector(getSoapId)
  const clientId = useSelector(getClientId)
  const patientId = useSelector(getPatientId)
  const patientName = useSelector(getPatientName(patientId))
  const modifierId = useSelector(getCurrentUserId)
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()
  const isFetching = useSelector(getFinanceIsFetching)
  const isFetchingChargeSheet = useSelector(getClientFinanceLoading)
  const isSaving = useSelector(getFinanceIsSaving)
  const isSendingOrders = useSelector(getOrdersIsSending)

  const [openConfirmAlert] = useConfirmAlert({
    type: ConfirmAlertType.SOAP_ORDER,
  })

  const isLoading =
    isFetching || isSaving || isSendingOrders || isFetchingChargeSheet

  const [localItems, setLocalItems] =
    useState<(InvoiceLineItem | RetailOrderLineItem)[]>(items)

  useEffect(() => {
    setLocalItems(items)
  }, [items])

  const handleDeleteClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const itemName = item.name || t('Common:ITEM')

    openConfirmAlert({
      message: t('Common:YOU_WILL_REMOVE_SOMETHING', {
        something: itemName,
      }),
      onConfirm: (proceed: boolean) => {
        if (proceed && clientId && modifierId) {
          const deleteItems = [
            {
              id: item.id,
              expectedModificationDate: item.modificationDate,
            },
          ]
          dispatch(
            deleteChargeSheetItems({
              items: deleteItems,
              modifierId,
              clientId,
            }),
          )
        }
      },
    })
  }

  const handleDeclineClick = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const isRetailOrder = getIsRetailOrderLineItem(item)

    if (isRetailOrder) {
      dispatch(
        editRetailOrderLineItem({
          id: item.id,
          quantity: item.quantity,
          declined: !item.declined,
          partNumber: item.partNumber,
          expectedModificationDate: item.modificationDate,
          logExpectedModificationDate: (item as RetailOrderLineItem)
            .prescription?.updatedAt,
          autoshipFrequency: (item as RetailOrderLineItem).autoshipFrequency,
          autoshipUnitId: (item as RetailOrderLineItem).autoshipUnit?.id,
        }),
      )
    } else {
      dispatch(
        editChargeSheetItem({
          updateItemInput: [
            {
              id: item.id,
              expectedModification: item.modificationDate,
              expectedSoapLogModification: (item as InvoiceLineItem)
                .soapLogModificationDate,
              declined: !item.declined,
            },
          ],
        }),
      )
    }
  }

  const handleBundleDeleteClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    const bundleItem = item as InvoiceLineItem

    openConfirmAlert({
      message: t('Soap:SOAP_ORDERS.CONFIRM_ALERT_MESSAGE'),
      onConfirm: (proceed: boolean) => {
        if (proceed && clientId && modifierId && bundleItem.items) {
          const deleteItems = bundleItem.items.map((childItem) => ({
            id: childItem.id,
            expectedModificationDate: childItem.modificationDate,
          }))
          dispatch(
            deleteChargeSheetItems({
              items: deleteItems,
              modifierId,
              clientId,
            }),
          )
        }
      },
    })
  }

  const handleBundleDeclineClick = (
    item: InvoiceLineItem | RetailOrderLineItem,
  ) => {
    const bundleItem = item as InvoiceLineItem
    if (bundleItem.items) {
      dispatch(
        editChargeSheetItem({
          updateItemInput: bundleItem.items.map((childItem) => ({
            id: childItem.id,
            expectedModification: childItem.modificationDate,
            expectedSoapLogModification: childItem.soapLogModificationDate,
            declined: !item.declined,
          })),
        }),
      )
    }
  }

  // Get item ID for drag and drop - must be unique
  const getItemId = (
    item: InvoiceLineItem | RetailOrderLineItem,
    index: number,
  ) => {
    if (isGroupedInvoiceItem(item)) {
      const invoiceItem = item as InvoiceLineItem
      // Use group or fallback to id or index
      const groupKey = invoiceItem.group || invoiceItem.id || `idx-${index}`
      return `charges-table-group-${invoiceItem.soapId || 'no-soap'}_${groupKey}`
    }
    // Use id + logId, or just id, or fallback to index
    const itemKey = item.id || `idx-${index}`
    const logKey = item.logId || 'no-log'
    return `charges-table-${itemKey}_${logKey}`
  }

  // Handle drag end for reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !chargesId) return

    const from = result.source.index
    const to = result.destination.index

    if (from === to) return

    const newItemsOrder = R.move(from, to, localItems)
    const flatLocalItems = R.chain(
      (i) =>
        !getIsRetailOrderLineItem(i) && (i as InvoiceLineItem).items
          ? [...((i as InvoiceLineItem).items || [])]
          : [i],
      localItems,
    )
    const newItemsOrderSlicedFlat = R.chain(
      (i) =>
        !getIsRetailOrderLineItem(i) && (i as InvoiceLineItem).items
          ? [...((i as InvoiceLineItem).items || [])]
          : [i],
      R.slice(0, R.max(from, to) + 1, newItemsOrder),
    )

    if (isChewyCheckoutEnabled) {
      const rearrangements = newItemsOrderSlicedFlat.reduce(
        (arr: GroupedLineItemArrangementInput[], item, idx) => {
          const lineItemType = getIsRetailOrderLineItem(item)
            ? LineItemType.RetailOrderLineItem
            : LineItemType.InvoiceLineItem
          if (
            item.id &&
            (item.id !== flatLocalItems[idx]?.id ||
              item.viewOrderNumber == null)
          ) {
            arr.push({
              lineItemId: item.id,
              lineItemType,
              viewOrderNumber: idx,
            })
          }
          return arr
        },
        [],
      )
      dispatch(rearrangeGroupedLineItems(chargesId, rearrangements))
    }

    setLocalItems(newItemsOrder)
  }

  // Toggle bundle expand/collapse
  const handleToggleExpand = (item: InvoiceLineItem | RetailOrderLineItem) => {
    const groupId = getIsRetailOrderLineItem(item)
      ? null
      : (item as InvoiceLineItem).group
    dispatch(toggleInvoiceGroupExpanded(groupId || item.id, soapId))
  }

  // Calculate total
  const total = getInvoiceGroupSubTotal(items)
  const displayPatientName = patientName || t('Common:PET')

  // Column definitions for skeleton rendering
  const columns = [
    { key: 'item', className: styles.dataCellItem },
    { key: 'producer', className: styles.dataCellProducer },
    { key: 'quantity', className: styles.dataCellQuantity },
    { key: 'actions', className: styles.dataCellActions },
    { key: 'total', className: styles.dataCellTotal },
  ]

  // Number of skeleton rows to show while loading (including bundle children)
  const skeletonRowCount =
    localItems.reduce((count, item) => {
      const isBundle = isGroupedInvoiceItem(item)
      const isRetailOrder = getIsRetailOrderLineItem(item)
      const bundleChildCount =
        isBundle && !isRetailOrder
          ? ((item as InvoiceLineItem).items?.length ?? 0)
          : 0
      return count + 1 + bundleChildCount
    }, 0) || 4

  // Hide table when no items and not loading
  // R.isEmpty(undefined) returns false, so we need explicit check
  const hasItems = items && items.length > 0
  if (!hasItems && !isLoading) {
    return null
  }

  return (
    <div
      className={classNames(styles.tableContainer, {
        [styles.tableContainerExpanded]: isExpanded,
      })}
    >
      <div
        className={classNames(styles.tableWrapper, {
          [styles.tableWrapperExpanded]: isExpanded,
        })}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr className={styles.headerRow}>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellItem,
                  )}
                >
                  {t('Common:ITEM')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellProducer,
                  )}
                >
                  {t('Common:PRODUCER_FINANCIAL')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellQuantity,
                  )}
                >
                  {t('Common:QUANTITY')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellActions,
                  )}
                >
                  {t('Common:ACTION_OTHER')}
                </th>
                <th
                  className={classNames(
                    styles.headerCell,
                    styles.headerCellTotal,
                  )}
                >
                  {t('Common:TOTAL')}
                </th>
              </tr>
            </thead>
            {isLoading ? (
              <tbody data-testid="charges-table-skeleton">
                {Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                  <tr
                    className={styles.skeletonRow}
                    key={`skeleton-${rowIndex}`}
                  >
                    {columns.map((col) => (
                      <td
                        className={classNames(styles.dataCell, col.className)}
                        key={col.key}
                      >
                        <KibSkeletonText lines={1} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              <Droppable droppableId="charges-table" type="CHARGES">
                {(droppableProvided) => (
                  <tbody
                    ref={(ref) => {
                      droppableProvided.innerRef(ref)
                    }}
                    {...droppableProvided.droppableProps}
                  >
                    {localItems.map((item, index) => {
                      const itemId = getItemId(item, index)
                      const isBundle = isGroupedInvoiceItem(item)
                      const isRetailOrder = getIsRetailOrderLineItem(item)
                      const bundleItems =
                        isBundle && !isRetailOrder
                          ? ((item as InvoiceLineItem).items ?? [])
                          : []

                      return (
                        <Draggable
                          draggableId={itemId}
                          index={index}
                          key={itemId}
                        >
                          {(draggableProvided, draggableSnapshot) => (
                            <ChirpSoapChargesTableRow
                              bundleItems={bundleItems}
                              dragHandleProps={
                                draggableProvided.dragHandleProps
                              }
                              draggableProps={draggableProvided.draggableProps}
                              editDisabled={editDisabled}
                              isBundle={isBundle}
                              isDragging={draggableSnapshot.isDragging}
                              item={item}
                              ref={draggableProvided.innerRef}
                              soapId={soapId}
                              onBundleDeclineClick={handleBundleDeclineClick}
                              onBundleDeleteClick={handleBundleDeleteClick}
                              onDeclineClick={handleDeclineClick}
                              onDeleteClick={handleDeleteClick}
                              onToggleExpand={handleToggleExpand}
                            />
                          )}
                        </Draggable>
                      )
                    })}
                    {droppableProvided.placeholder}
                  </tbody>
                )}
              </Droppable>
            )}
          </table>
        </DragDropContext>
      </div>

      {/* Footer with total */}
      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <span className={styles.totalLabel}>
            {t('Common:PATIENT_TOTAL_WITH_VALUE', {
              patientName: displayPatientName,
              amount: NumberUtils.formatMoney(total),
            })}
          </span>
        </div>
      </div>
    </div>
  )
}

export default ChirpSoapChargesTable
